import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api, { fmtDate } from '../../utils/api';

const SCOPE_GROUPS_HE = [
  { key: 'orders', label: 'הזמנות', scopes: [
    { value: 'orders:read', label: 'כל ההזמנות' },
    { value: 'orders:read:si', label: 'רק SI (חשבוניות)' },
    { value: 'orders:read:so', label: 'רק SO (פתוחות)' },
    { value: 'orders:read:comp', label: 'רק compensations' },
  ]},
  { key: 'prices', label: 'מחירים', scopes: [
    { value: 'prices:read', label: 'כל המחירים' },
    { value: 'prices:read:agreed', label: 'מחירים מוסכמים' },
    { value: 'prices:read:deals', label: 'נתוני deals' },
    { value: 'prices:read:credits', label: 'credits/rebate' },
  ]},
  { key: 'deals', label: 'Deals', scopes: [{ value: 'deals:read', label: 'כל ה-deals' }]},
  { key: 'logistics', label: 'לוגיסטיקה', scopes: [{ value: 'logistics:read', label: 'מידע שילוח' }]},
  { key: 'warehouse', label: 'מלאי', scopes: [
    { value: 'warehouse:read', label: 'כל המלאי' },
    { value: 'warehouse:read:snt', label: 'רק S&T' },
    { value: 'warehouse:read:shager', label: 'רק Shager' },
    { value: 'warehouse:read:future', label: 'רק Future SI' },
  ]},
  { key: 'qr', label: 'QR Serials', scopes: [{ value: 'qr:read', label: 'QR serials' }]},
  { key: 'customers', label: 'לקוחות', scopes: [{ value: 'customers:read', label: 'רשימת לקוחות' }]},
  { key: 'expenses', label: 'הוצאות', scopes: [{ value: 'expenses:read', label: 'הוצאות + payment due' }]},
  { key: 'profitability', label: 'רווחיות', scopes: [{ value: 'profitability:read', label: 'נתוני רווחיות' }]},
  { key: 'fc', label: 'FC Orders', scopes: [{ value: 'fc:read', label: 'FC orders' }]},
  { key: 'products', label: 'מוצרים', scopes: [{ value: 'products:read', label: 'SKU ↔ barcode' }]},
  { key: 'dashboard', label: 'Dashboard', scopes: [{ value: 'dashboard:read', label: 'נתוני סיכום' }]},
  { key: 'rma', label: 'RMA', scopes: [{ value: 'rma:read', label: 'בקשות RMA' }]},
];

const SCOPE_GROUPS_EN = [
  { key: 'orders', label: 'Orders', scopes: [
    { value: 'orders:read', label: 'All orders' },
    { value: 'orders:read:si', label: 'SI only (invoices)' },
    { value: 'orders:read:so', label: 'SO only (pending)' },
    { value: 'orders:read:comp', label: 'Compensations only' },
  ]},
  { key: 'prices', label: 'Prices', scopes: [
    { value: 'prices:read', label: 'All prices' },
    { value: 'prices:read:agreed', label: 'Agreed prices' },
    { value: 'prices:read:deals', label: 'Deals data' },
    { value: 'prices:read:credits', label: 'Credits / rebate' },
  ]},
  { key: 'deals', label: 'Deals', scopes: [{ value: 'deals:read', label: 'All deals' }]},
  { key: 'logistics', label: 'Logistics', scopes: [{ value: 'logistics:read', label: 'Shipping info' }]},
  { key: 'warehouse', label: 'Warehouse', scopes: [
    { value: 'warehouse:read', label: 'All stock' },
    { value: 'warehouse:read:snt', label: 'S&T only' },
    { value: 'warehouse:read:shager', label: 'Shager only' },
    { value: 'warehouse:read:future', label: 'Future SI only' },
  ]},
  { key: 'qr', label: 'QR Serials', scopes: [{ value: 'qr:read', label: 'QR serials' }]},
  { key: 'customers', label: 'Customers', scopes: [{ value: 'customers:read', label: 'Customer list' }]},
  { key: 'expenses', label: 'Expenses', scopes: [{ value: 'expenses:read', label: 'Expenses + payment due' }]},
  { key: 'profitability', label: 'Profitability', scopes: [{ value: 'profitability:read', label: 'Profitability data' }]},
  { key: 'fc', label: 'FC Orders', scopes: [{ value: 'fc:read', label: 'FC orders' }]},
  { key: 'products', label: 'Products', scopes: [{ value: 'products:read', label: 'SKU ↔ barcode' }]},
  { key: 'dashboard', label: 'Dashboard', scopes: [{ value: 'dashboard:read', label: 'Summary data' }]},
  { key: 'rma', label: 'RMA', scopes: [{ value: 'rma:read', label: 'RMA requests' }]},
];

const EXPIRY_OPTIONS = (he) => [
  { label: he ? 'ללא פג תוקף' : 'No expiry', days: null },
  { label: he ? '24 שעות' : '24 hours', days: 1 },
  { label: he ? '7 ימים' : '7 days', days: 7 },
  { label: he ? '30 ימים' : '30 days', days: 30 },
  { label: he ? '90 ימים (מומלץ)' : '90 days (recommended)', days: 90 },
  { label: he ? '180 ימים' : '180 days', days: 180 },
  { label: he ? 'שנה' : '1 year', days: 365 },
  { label: he ? 'שנתיים' : '2 years', days: 730 },
  { label: he ? 'תאריך מותאם' : 'Custom date', days: 'custom' },
];

export default function ApiKeysTab() {
  const { i18n } = useTranslation();
  const he = i18n.language === 'he';
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [viewingUsage, setViewingUsage] = useState(null);

  const fetchKeys = useCallback(async () => {
    try {
      const res = await api.get('/api-keys');
      setKeys(res.data.keys || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleRevoke = async (id) => {
    if (!window.confirm(he ? 'לבטל את המפתח? לא ניתן לבטל ביטול.' : 'Revoke this key? This cannot be undone.')) return;
    try {
      await api.post(`/api-keys/${id}/revoke`);
      fetchKeys();
    } catch (err) { alert((he ? 'שגיאה: ' : 'Error: ') + err.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(he ? 'למחוק לצמיתות? זה יסיר גם את כל היסטוריית השימוש.' : 'Delete permanently? This also removes all usage history.')) return;
    try {
      await api.delete(`/api-keys/${id}`);
      fetchKeys();
    } catch (err) { alert((he ? 'שגיאה: ' : 'Error: ') + err.message); }
  };

  const handleRotate = async (id) => {
    const graceDays = window.prompt(
      he ? 'תקופת חסד לפני שהמפתח הישן מתבטל (ימים, 0-90):' : 'Grace period before old key revokes (days, 0-90):',
      '7'
    );
    if (graceDays === null) return;
    const grace = parseInt(graceDays, 10);
    if (!Number.isFinite(grace) || grace < 0 || grace > 90) {
      alert(he ? 'ערך לא תקין' : 'Invalid value'); return;
    }
    try {
      const res = await api.post(`/api-keys/${id}/rotate`, { grace_period_days: grace });
      setNewKey({ ...res.data, isRotation: true });
      fetchKeys();
    } catch (err) { alert((he ? 'שגיאה: ' : 'Error: ') + err.message); }
  };

  const statusOf = (k) => {
    if (k.revoked_at) {
      if (k.grace_period_ends_at && new Date(k.grace_period_ends_at) > new Date()) {
        return {
          label: (he ? '⏳ חסד עד ' : '⏳ Grace until ') + fmtDate(k.grace_period_ends_at),
          color: '#f59e0b',
        };
      }
      return { label: he ? '🚫 מבוטל' : '🚫 Revoked', color: '#dc2626' };
    }
    if (!k.is_active) return { label: he ? '⏸️ מושבת' : '⏸️ Disabled', color: '#6b7280' };
    if (k.expires_at && new Date(k.expires_at) < new Date()) {
      return { label: he ? '⏰ פג תוקף' : '⏰ Expired', color: '#dc2626' };
    }
    return { label: he ? '✅ פעיל' : '✅ Active', color: '#16a34a' };
  };

  if (loading) return <div className="loading"><div className="spinner" />{he ? 'טוען...' : 'Loading...'}</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0 }}>🔑 API Keys</h3>
          <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>
            {he
              ? 'מפתחות קריאה בלבד למערכות חיצוניות. כל מפתח מוצג פעם אחת בלבד — שמור אותו מיד ביצירה.'
              : 'Read-only keys for external systems. Each key is shown once — save it immediately upon creation.'}
          </p>
        </div>
        <button className="btn btn-gold" onClick={() => setShowCreate(true)}>
          {he ? '+ מפתח חדש' : '+ New key'}
        </button>
      </div>

      {keys.length === 0 ? (
        <div className="empty-state">{he ? 'אין מפתחות עדיין. צור את הראשון.' : 'No keys yet. Create the first one.'}</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{he ? 'שם' : 'Name'}</th>
                <th>{he ? 'מפתח (prefix)' : 'Key (prefix)'}</th>
                <th>{he ? 'הרשאות' : 'Scopes'}</th>
                <th>{he ? 'סטטוס' : 'Status'}</th>
                <th>{he ? 'שימוש אחרון' : 'Last used'}</th>
                <th>{he ? 'פג תוקף' : 'Expires'}</th>
                <th>{he ? 'סה"כ קריאות' : 'Total calls'}</th>
                <th>{he ? 'פעולות' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {keys.map(k => {
                const s = statusOf(k);
                const scopesList = k.scopes || [];
                return (
                  <tr key={k.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{k.name}</div>
                      {k.description && <div style={{ fontSize: 11, color: '#64748b' }}>{k.description}</div>}
                    </td>
                    <td><code style={{ fontSize: 12 }}>{k.key_prefix}...</code></td>
                    <td>
                      {scopesList.includes('*:read') ? (
                        <span style={{ background: '#fef3c7', padding: '2px 8px', borderRadius: 4, fontSize: 11 }}>
                          🌐 {he ? 'הכל' : 'All'}
                        </span>
                      ) : scopesList.length === 0 ? (
                        <span style={{ color: '#dc2626', fontSize: 11 }}>
                          {he ? 'אין הרשאות' : 'No scopes'}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11 }}>
                          {scopesList.length} {he ? 'הרשאות' : 'scopes'}
                        </span>
                      )}
                    </td>
                    <td><span style={{ color: s.color, fontWeight: 600, fontSize: 12 }}>{s.label}</span></td>
                    <td style={{ fontSize: 12 }}>{k.last_used_at ? fmtDate(k.last_used_at) : '—'}</td>
                    <td style={{ fontSize: 12 }}>{k.expires_at ? fmtDate(k.expires_at) : (he ? 'ללא' : 'None')}</td>
                    <td>{k.total_calls || 0}</td>
                    <td>
                      <button className="btn btn-sm btn-outline" onClick={() => setViewingUsage(k)} style={{ fontSize: 10 }}>
                        📊 {he ? 'שימוש' : 'Usage'}
                      </button>
                      {!k.revoked_at && (
                        <>
                          <button className="btn btn-sm btn-outline" onClick={() => handleRotate(k.id)}
                            style={{ fontSize: 10, marginInlineStart: 4 }}>🔄 Rotate</button>
                          <button className="btn btn-sm btn-outline" onClick={() => handleRevoke(k.id)}
                            style={{ fontSize: 10, marginInlineStart: 4, color: '#dc2626' }}>
                            🚫 {he ? 'בטל' : 'Revoke'}
                          </button>
                        </>
                      )}
                      <button className="btn btn-sm btn-outline" onClick={() => handleDelete(k.id)}
                        style={{ fontSize: 10, marginInlineStart: 4, color: '#991b1b' }}>🗑</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateKeyModal
          he={he}
          onClose={() => setShowCreate(false)}
          onCreated={(data) => { setShowCreate(false); setNewKey(data); fetchKeys(); }}
        />
      )}

      {newKey && <NewKeyModal he={he} data={newKey} onClose={() => setNewKey(null)} />}
      {viewingUsage && <UsageLogModal he={he} apiKey={viewingUsage} onClose={() => setViewingUsage(null)} />}
    </div>
  );
}

function CreateKeyModal({ he, onClose, onCreated }) {
  const [mode, setMode] = useState('custom');
  const [selectedScopes, setSelectedScopes] = useState(new Set());
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [expiryChoice, setExpiryChoice] = useState(90);
  const [customDate, setCustomDate] = useState('');
  const [rateLimit, setRateLimit] = useState(60);
  const [submitting, setSubmitting] = useState(false);

  const SCOPE_GROUPS = he ? SCOPE_GROUPS_HE : SCOPE_GROUPS_EN;
  const EXPIRY = EXPIRY_OPTIONS(he);

  const toggleScope = (s) => {
    const next = new Set(selectedScopes);
    next.has(s) ? next.delete(s) : next.add(s);
    setSelectedScopes(next);
  };

  const handleSubmit = async () => {
    if (!name.trim() || name.trim().length < 2) {
      alert(he ? 'שם חובה (לפחות 2 תווים)' : 'Name required (at least 2 chars)');
      return;
    }

    let scopes = [];
    if (mode === 'all') scopes = ['*:read'];
    else if (mode === 'none') scopes = [];
    else scopes = [...selectedScopes];

    let expires_at = null;
    if (expiryChoice === 'custom') {
      if (!customDate) { alert(he ? 'בחר תאריך' : 'Pick a date'); return; }
      expires_at = customDate;
    } else if (expiryChoice !== null) {
      const d = new Date(); d.setDate(d.getDate() + expiryChoice);
      expires_at = d.toISOString();
    }

    setSubmitting(true);
    try {
      const res = await api.post('/api-keys', {
        name: name.trim(),
        description: description.trim() || null,
        scopes,
        expires_at,
        rate_limit_per_minute: rateLimit,
      });
      onCreated(res.data);
    } catch (err) {
      alert((he ? 'שגיאה: ' : 'Error: ') + (err.response?.data?.error || err.message));
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 700, maxHeight: '90vh', overflow: 'auto' }}>
        <h3>{he ? 'יצירת API Key חדש' : 'Create new API Key'}</h3>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            {he ? 'שם *' : 'Name *'}
          </label>
          <input className="input" value={name} onChange={e => setName(e.target.value)}
            placeholder={he ? 'למשל: Sandt Integration' : 'e.g. Sandt Integration'} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            {he ? 'תיאור (אופציונלי)' : 'Description (optional)'}
          </label>
          <textarea className="input" value={description} onChange={e => setDescription(e.target.value)}
            rows={2} placeholder={he ? 'למה המפתח הזה משמש...' : 'What this key is used for...'} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
            {he ? 'הרשאות' : 'Scopes'}
          </label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <button className={`btn btn-sm ${mode === 'all' ? 'btn-gold' : 'btn-outline'}`}
              onClick={() => setMode('all')}>🌐 {he ? 'הכל (*:read)' : 'All (*:read)'}</button>
            <button className={`btn btn-sm ${mode === 'custom' ? 'btn-gold' : 'btn-outline'}`}
              onClick={() => setMode('custom')}>⚙️ {he ? 'מותאם' : 'Custom'}</button>
            <button className={`btn btn-sm ${mode === 'none' ? 'btn-gold' : 'btn-outline'}`}
              onClick={() => setMode('none')}>🚫 {he ? 'כלום' : 'None'}</button>
          </div>
          {mode === 'all' && (
            <div style={{ fontSize: 12, color: '#f59e0b', padding: 8, background: '#fef3c7', borderRadius: 4 }}>
              {he
                ? '⚠️ המפתח יקבל גישה לכל ה-endpoints לקריאה. השתמש בזהירות.'
                : '⚠️ Key will have access to all read endpoints. Use carefully.'}
            </div>
          )}
          {mode === 'none' && (
            <div style={{ fontSize: 12, color: '#6b7280', padding: 8, background: '#f3f4f6', borderRadius: 4 }}>
              {he
                ? 'המפתח ייווצר אבל לא יוכל לגשת לשום דבר. תוכל לעדכן הרשאות אחר כך.'
                : 'Key will be created but cannot access anything. You can update scopes later.'}
            </div>
          )}
          {mode === 'custom' && (
            <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 4, padding: 8 }}>
              {SCOPE_GROUPS.map(g => (
                <div key={g.key} style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{g.label}</div>
                  {g.scopes.map(s => (
                    <label key={s.value} style={{ display: 'block', fontSize: 12, marginInlineStart: 12, padding: '2px 0' }}>
                      <input type="checkbox" checked={selectedScopes.has(s.value)} onChange={() => toggleScope(s.value)}
                        style={{ marginInlineEnd: 6 }} />
                      <code style={{ fontSize: 11, color: '#1565c0' }}>{s.value}</code>
                      <span style={{ marginInlineStart: 8, color: '#64748b' }}>{s.label}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            {he ? 'פג תוקף' : 'Expiry'}
          </label>
          <select className="input" value={expiryChoice === null ? 'null' : expiryChoice}
            onChange={e => setExpiryChoice(e.target.value === 'null' ? null : e.target.value === 'custom' ? 'custom' : parseInt(e.target.value, 10))}>
            {EXPIRY.map(o => (
              <option key={o.label} value={o.days === null ? 'null' : o.days}>{o.label}</option>
            ))}
          </select>
          {expiryChoice === 'custom' && (
            <input className="input" type="datetime-local" value={customDate}
              onChange={e => setCustomDate(e.target.value)} style={{ marginTop: 8 }} />
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            {he ? 'Rate Limit (קריאות לדקה):' : 'Rate Limit (calls per minute):'} {rateLimit}
          </label>
          <input type="range" min="10" max="600" step="10" value={rateLimit}
            onChange={e => setRateLimit(parseInt(e.target.value, 10))} style={{ width: '100%' }} />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-outline" onClick={onClose} disabled={submitting}>
            {he ? 'ביטול' : 'Cancel'}
          </button>
          <button className="btn btn-gold" onClick={handleSubmit} disabled={submitting}>
            {submitting ? (he ? 'יוצר...' : 'Creating...') : (he ? 'צור מפתח' : 'Create key')}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewKeyModal({ he, data, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.plainKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };
  return (
    <div className="modal-backdrop" onClick={(e) => e.stopPropagation()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <h3>🔑 {data.isRotation
          ? (he ? 'מפתח חדש נוצר (Rotate)' : 'New key created (Rotate)')
          : (he ? 'המפתח החדש שלך' : 'Your new key')}</h3>
        <div style={{ background: '#fef3c7', border: '2px solid #f59e0b', padding: 12, borderRadius: 6, marginBottom: 16 }}>
          <strong>⚠️ {he ? 'שמור את המפתח עכשיו!' : 'Save the key now!'}</strong>
          <div style={{ fontSize: 13, marginTop: 4 }}>
            {he
              ? 'לאחר סגירת החלון לא תוכל לראות את המפתח שוב. שמור במקום בטוח.'
              : 'After closing this dialog you will not be able to see this key again. Save it somewhere safe.'}
          </div>
        </div>
        <div style={{ background: '#1f2937', color: '#f9fafb', padding: 12, borderRadius: 4, fontFamily: 'monospace',
          fontSize: 12, wordBreak: 'break-all', marginBottom: 12 }}>
          {data.plainKey}
        </div>
        <button className="btn btn-gold" onClick={copy} style={{ width: '100%' }}>
          {copied ? (he ? '✓ הועתק!' : '✓ Copied!') : (he ? '📋 העתק' : '📋 Copy')}
        </button>
        {data.isRotation && data.oldKeyGraceEndsAt && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#64748b' }}>
            {he ? 'המפתח הישן ישאר פעיל עד ' : 'Old key remains active until '}
            {fmtDate(data.oldKeyGraceEndsAt)}.
          </div>
        )}
        <button className="btn btn-outline" onClick={onClose} style={{ marginTop: 12, width: '100%' }}>
          {he ? 'סגור (שמרת?)' : 'Close (saved?)'}
        </button>
      </div>
    </div>
  );
}

function UsageLogModal({ he, apiKey, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/api-keys/${apiKey.id}/usage`).then(res => setData(res.data)).catch(() => setData({ logs: [], stats: {} }));
  }, [apiKey.id]);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 900, maxHeight: '90vh', overflow: 'auto' }}>
        <h3>📊 {he ? 'שימוש' : 'Usage'} — {apiKey.name}</h3>
        {!data ? (
          <div className="loading"><div className="spinner" />{he ? 'טוען...' : 'Loading...'}</div>
        ) : (
          <>
            <div className="summary-cards" style={{ marginBottom: 16 }}>
              <div className="summary-card">
                <div className="label">{he ? '24 שעות' : 'Last 24h'}</div>
                <div className="value">{data.stats?.last_24h || 0}</div>
              </div>
              <div className="summary-card">
                <div className="label">{he ? '7 ימים' : 'Last 7d'}</div>
                <div className="value">{data.stats?.last_7d || 0}</div>
              </div>
              <div className="summary-card">
                <div className="label">{he ? 'סה"כ' : 'Total'}</div>
                <div className="value">{data.stats?.total_calls || 0}</div>
              </div>
              <div className="summary-card">
                <div className="label">{he ? 'שגיאות' : 'Errors'}</div>
                <div className="value" style={{ color: '#dc2626' }}>{data.stats?.total_errors || 0}</div>
              </div>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{he ? 'זמן' : 'Time'}</th>
                    <th>Endpoint</th>
                    <th>Method</th>
                    <th>Status</th>
                    <th>IP</th>
                    <th>Response</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.logs || []).map(l => (
                    <tr key={l.id}>
                      <td style={{ fontSize: 11 }}>{fmtDate(l.called_at)}</td>
                      <td><code style={{ fontSize: 11 }}>{l.endpoint}</code></td>
                      <td style={{ fontSize: 11 }}>{l.method}</td>
                      <td><span style={{ color: l.status_code >= 400 ? '#dc2626' : '#16a34a', fontWeight: 600, fontSize: 11 }}>{l.status_code}</span></td>
                      <td style={{ fontSize: 11 }}>{l.ip_address || '—'}</td>
                      <td style={{ fontSize: 11 }}>{l.response_ms}ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <button className="btn btn-outline" onClick={onClose} style={{ marginTop: 12 }}>
          {he ? 'סגור' : 'Close'}
        </button>
      </div>
    </div>
  );
}
